import { useCallback, useEffect, useState } from 'react';
import Dropdown from '../components/Dropdown';
import Field from '../components/Field';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import SearchableSelect from '../components/SearchableSelect';
import StatusBadge from '../components/StatusBadge';
import { ResetPasswordModal, UserEditModal } from '../components/UserAccountModals';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import useForm from '../hooks/useForm';
import * as v from '../validation/rules';
import { REGISTRATION_CODES } from '../validation/serverCodes';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Staff, and only staff. A teacher is created from Registered Users, where the teachers
 * are and where the registration form they need is — this screen asks for a name, a
 * number and a role, which is not a teacher.
 */
const INTERNAL_ROLES = [
  { value: 'SUPPORT_EXECUTIVE', label: 'Support Executive' },
  { value: 'SALES_REP', label: 'Sales Representative' },
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
];

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

const EMPTY_DRAFT = { fullName: '', mobile: '', email: '', role: 'SUPPORT_EXECUTIVE', password: '' };

/**
 * Whether this administrator may edit or reset that account.
 *
 * <p>Mirrors `AdminScope` on the server, which is the one that counts. A Super Admin
 * account may only be taken over by another Super Admin: a Support Executive who could
 * change its email or its password could recover it, and the content library is
 * supposed to be the one thing their role cannot reach.
 */
const mayTakeOver = (actor, target) =>
  target?.role !== 'SUPER_ADMIN' || actor?.role === 'SUPER_ADMIN';

/**
 * A Sales Representative's territory is a set of states, and it is what decides what
 * they can see — schools, teachers, students, codes and every number those add up to.
 * A rep with none sees nothing, so the form says so rather than leaving an empty field
 * to be read as "everywhere".
 */
function TerritoryField({ states, onChange, searchStates }) {
  return (
    <Field
      label="Territory"
      id="stateIds"
      hint="The states this rep covers. Everything in them is theirs to see; a rep with no states sees nothing."
    >
      <SearchableSelect
        id="stateIds"
        multiple
        value={states}
        onChange={onChange}
        loadOptions={searchStates}
        placeholder="Search states"
        emptyLabel="No state matches"
      />
    </Field>
  );
}

export default function InternalUsersPage() {
  const { user: currentUser } = useAuth();
  const [page, setPage] = useState({ items: [], page: 0, totalItems: 0, hasNext: false });
  const [filters, setFilters] = useState({ role: '', status: '', query: '' });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [territoryStates, setTerritoryStates] = useState([]);
  // The rep whose territory is being edited, and the working set for that modal.
  const [editingTerritory, setEditingTerritory] = useState(null);
  const [editingStates, setEditingStates] = useState([]);
  const [territoryError, setTerritoryError] = useState(null);
  const [savingTerritory, setSavingTerritory] = useState(false);
  // The account being corrected, and the one being given a new password.
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);

  const searchStates = useCallback((q) => api.lookupStates(q), []);

  const createForm = useForm({
    initialValues: EMPTY_DRAFT,
    validators: {
      fullName: v.fullName,
      mobile: v.mobile,
      email: v.email,
      role: v.required('Role'),
      // Blank means "generate one", so the rule only applies to a supplied password.
      password: v.optional(v.password),
    },
  });

  const load = useCallback(
    async (pageNumber = 0) => {
      setLoading(true);
      try {
        // `group` rather than the role filter alone: an admin who picks "All roles"
        // still gets staff only, and no combination of filters can surface a student
        // on the screen for creating a Support Executive.
        setPage(await api.adminListUsers({ ...filters, group: 'INTERNAL', page: pageNumber, size: 20 }));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    // Debounced so typing in the search box does not fire a request per keystroke.
    const handle = setTimeout(() => load(0), 250);
    return () => clearTimeout(handle);
  }, [load]);

  const createUser = createForm.handleSubmit(async (values) => {
    setCreateError(null);
    try {
      const created = await api.adminCreateUser({
        fullName: values.fullName.trim(),
        mobile: v.normalizeMobile(values.mobile),
        email: v.normalizeEmail(values.email),
        role: values.role,
        password: values.password || null,
        // Sent only for a rep: the server refuses states on any other role rather than
        // dropping them, and a stale set from a role switch would be exactly that.
        stateIds: values.role === 'SALES_REP' ? territoryStates.map((state) => state.id) : [],
      });
      setShowCreate(false);
      createForm.reset(EMPTY_DRAFT);
      setTerritoryStates([]);
      setGeneratedPassword(created.generatedPassword ?? null);
      setToast(`${created.user.fullName} created — invite sent`);
      await load(0);
    } catch (err) {
      if (!createForm.applyServerErrors(err, REGISTRATION_CODES)) setCreateError(err);
    }
  });

  function openTerritory(row) {
    setTerritoryError(null);
    setEditingTerritory(row);
    setEditingStates(row.territoryStates ?? []);
  }

  async function saveTerritory() {
    setSavingTerritory(true);
    setTerritoryError(null);
    try {
      const updated = await api.adminUpdateTerritory(
        editingTerritory.id,
        editingStates.map((state) => state.id),
      );
      setEditingTerritory(null);
      setToast(
        updated.territoryStates?.length
          ? `${updated.fullName} covers ${updated.territoryStates.length} state(s)`
          : `${updated.fullName} now covers no states and sees nothing`,
      );
      await load(page.page);
    } catch (err) {
      setTerritoryError(err);
    } finally {
      setSavingTerritory(false);
    }
  }

  async function toggleStatus(row) {
    const next = row.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
    try {
      await api.adminUpdateStatus(row.id, next);
      setToast(`${row.fullName} is now ${next.toLowerCase()}`);
      await load(page.page);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Internal Users</h1>
          <p>
            Support, Sales and Admin accounts are created here — those roles have no public
            self-registration. Teachers and students are on Registered Users, which is also
            where a teacher is created for a school that hands over a list.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Icon name="plus" size={15} />
          New internal user
        </button>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      {generatedPassword && (
        <InfoBanner>
          Temporary password: <strong className="mono">{generatedPassword}</strong> — shown once, and
          only because this environment enables it. They must change it at their next sign-in.
        </InfoBanner>
      )}

      <section className="card card-pad">
        <div className="toolbar">
          <div style={{ width: 200 }}>
            <Dropdown
              id="roleFilter"
              options={[{ value: '', label: 'All roles' }, ...INTERNAL_ROLES]}
              value={filters.role}
              onChange={(role) => setFilters({ ...filters, role })}
              ariaLabel="Filter by role"
            />
          </div>
          <div style={{ width: 170 }}>
            <Dropdown
              id="statusFilter"
              options={STATUSES}
              value={filters.status}
              onChange={(status) => setFilters({ ...filters, status })}
              ariaLabel="Filter by status"
            />
          </div>
          <input
            className="input"
            style={{ flex: '1 1 220px' }}
            placeholder="Search name, mobile or email"
            aria-label="Search users"
            maxLength={100}
            value={filters.query}
            onChange={(event) => setFilters({ ...filters, query: event.target.value })}
          />
        </div>
      </section>

      <section className="card">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Mobile</th>
                <th>Territory</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {page.items.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="cell-user">
                      <div>
                        {row.fullName}
                        <span>{row.email ?? '—'}</span>
                      </div>
                    </div>
                  </td>
                  <td>{row.roleLabel}</td>
                  <td className="mono">{row.mobile}</td>
                  <td>{territoryLabel(row)}</td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {row.role === 'SALES_REP' && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ marginRight: 'var(--s0)' }}
                        onClick={() => openTerritory(row)}
                      >
                        States
                      </button>
                    )}
                    {/* The API refuses these two for a Support Executive against a
                        Super Admin — changing the email an account is recovered
                        through, or its password, hands that account over. Hidden
                        here as well so nobody finds out by being refused. */}
                    {mayTakeOver(currentUser, row) && (
                      <>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setEditing(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={row.id === currentUser?.id}
                          title={row.id === currentUser?.id
                            ? 'Change your own password from your profile'
                            : undefined}
                          onClick={() => setResetting(row)}
                        >
                          Reset password
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className={`btn btn-sm ${row.status === 'SUSPENDED' ? 'btn-secondary' : 'btn-danger'}`}
                      disabled={row.id === currentUser?.id}
                      title={row.id === currentUser?.id ? 'You cannot suspend your own account' : undefined}
                      onClick={() => toggleStatus(row)}
                    >
                      {row.status === 'SUSPENDED' ? 'Reactivate' : 'Suspend'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div className="empty">Loading…</div>}
        {!loading && page.items.length === 0 && <div className="empty">No accounts match these filters.</div>}

        <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}>
          <span className="muted small">{page.totalItems} account(s)</span>
          <span style={{ display: 'flex', gap: 'var(--s0)' }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={page.page === 0}
                    onClick={() => load(page.page - 1)}>
              Previous
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!page.hasNext}
                    onClick={() => load(page.page + 1)}>
              Next
            </button>
          </span>
        </div>
      </section>

      {showCreate && (
        <Modal
          title="New internal user"
          subtitle="Support, Sales and Admin accounts are admin-created"
          width={640}
          onClose={() => setShowCreate(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button type="submit" form="create-user" className="btn btn-primary" disabled={createForm.submitting}>
                <Icon name="check" size={15} />
                {createForm.submitting ? 'Creating…' : 'Create account'}
              </button>
            </>
          }
        >
          <form id="create-user" {...createForm.formProps} onSubmit={createUser} className="stack">
            <ErrorBanner error={createError} onDismiss={() => setCreateError(null)} />
            <div className="grid-2">
              <Field label="Full name" id="fullName" required error={createForm.errorFor('fullName')}>
                <input className="input" autoComplete="name" maxLength={100} {...createForm.field('fullName')} />
              </Field>
              <Field label="Role" id="role" required error={createForm.errorFor('role')}>
                <Dropdown
                  id="role"
                  required
                  options={INTERNAL_ROLES}
                  value={createForm.values.role}
                  onChange={(role) => createForm.setValue('role', role)}
                />
              </Field>
            </div>
            <div className="grid-2">
              <Field label="Mobile number" id="mobile" required error={createForm.errorFor('mobile')}>
                <input className="input" {...v.mobileField(createForm)} />
              </Field>
              <Field label="Email address" id="email" required error={createForm.errorFor('email')}>
                <input className="input" type="email" autoComplete="email" maxLength={255}
                       {...createForm.field('email')} />
              </Field>
            </div>
            <Field
              label="Initial password"
              id="password"
              hint="Leave blank to generate one and email it; the user must change it at first sign-in"
              error={createForm.errorFor('password')}
            >
              <input className="input" type="password" autoComplete="new-password" maxLength={100}
                     {...createForm.field('password')} />
            </Field>
            {createForm.values.role === 'SALES_REP' && (
              <TerritoryField
                states={territoryStates}
                onChange={setTerritoryStates}
                searchStates={searchStates}
              />
            )}
          </form>
        </Modal>
      )}

      {editing && (
        <UserEditModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={async (saved) => {
            setEditing(null);
            await load(page.page);
            setToast(`${saved.fullName} updated`);
          }}
        />
      )}

      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={async (result) => {
            setResetting(null);
            // Only ever present where the environment exposes generated credentials;
            // everywhere else the password reaches them by email alone.
            setGeneratedPassword(result.generatedPassword ?? null);
            await load(page.page);
            setToast(`${result.user.fullName} must choose a new password at next sign-in`);
          }}
        />
      )}

      {editingTerritory && (
        <Modal
          title={`Territory · ${editingTerritory.fullName}`}
          subtitle="The states this rep covers, and everything standing in them"
          width={560}
          onClose={() => setEditingTerritory(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setEditingTerritory(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={savingTerritory}
                      onClick={saveTerritory}>
                <Icon name="check" size={15} />
                {savingTerritory ? 'Saving…' : 'Save territory'}
              </button>
            </>
          }
        >
          <div className="stack">
            <ErrorBanner error={territoryError} onDismiss={() => setTerritoryError(null)} />
            <TerritoryField
              states={editingStates}
              onChange={setEditingStates}
              searchStates={searchStates}
            />
          </div>
        </Modal>
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}

/**
 * What a row's Territory cell reads. Only a rep has one, and "None" is said out loud
 * because it is the state in which that rep can see nothing at all.
 */
function territoryLabel(row) {
  if (row.role !== 'SALES_REP') return <span className="muted">—</span>;
  const states = row.territoryStates ?? [];
  if (states.length === 0) return <span className="badge badge-warn">None</span>;
  if (states.length <= 2) return states.map((state) => state.name).join(', ');
  return `${states[0].name} +${states.length - 1} more`;
}
